/* eslint-disable react-hooks/exhaustive-deps */
import ToggleBookmarkButton from "components/bookmarks/ToggleBookmarkButton";
import BackToTop from "components/qol/BackToTop";
import Rating from "components/rating/Rating";
import BrazilianRealEstateFundFundamentalsChart from "components/realestatefunds/charts/BrazilianRealEstateFundFundamentalsChart";
import formatMoneyValue from "components/utils/formatMoneyValue";
import { format } from "date-fns";
import { shades } from "lib/miolo";
import parseDateYmd from "lib/utils/parseDateYmd";
import { default as Head } from "next/head";
import { useEffect, useState } from "react";
import { useAmplitude } from "../../lib/amplitude/Amplitude";
import type { IBrazilianRealEstateFundTopLevelFragment } from "../../lib/fragments/__generated__/BrazilianRealEstateFundTopLevel.generated";
import useSelfie from "../../lib/hooks/useSelfie";
import { IAssetNamespace, IAssetType } from "../../types/graphqlTypes";
import YouTubeAnalysisVideo from "../company/YouTubeAnalysisVideo";
import AssetDiscussion from "../discussions/AssetDiscussion";
import AssetDiscussionGridContainer from "../discussions/AssetDiscussionGridContainer";
import Liquidity from "../liquidity/Liquidity";
import * as palette from "../palette";
import DistributionOfAssets from "./DistributionOfAssets";
import PropertiesToRent from "./PropertiesToRent";
import RealEstateFundFinancials from "./RealEstateFundFinancials";

interface IBrazilianRealEstateFundProps {
  realEstateFund: IBrazilianRealEstateFundTopLevelFragment;
  tickerSymbol: string;
}

export default function BrazilianRealEstateFund({ realEstateFund, tickerSymbol }: IBrazilianRealEstateFundProps) {
  const user = useSelfie();
  const amplitude = useAmplitude();
  const [showCompensationPolicy, setShowCompensationPolicy] = useState(false);

  useEffect(() => {
    amplitude.logEvent("Viewed issuer", {
      category: "FIIs",
      issuerId: realEstateFund._id,
      tickerSymbol: tickerSymbol.toUpperCase(),
      name: realEstateFund.name,
      sector: realEstateFund.selfRegulation.primarySegment,
    });
  }, [realEstateFund._id]);

  if (!user) {
    return null;
  }

  return (
    <div
      css={{
        padding: "48px 0",
        display: "grid",
        gridRowGap: 32,
      }}
    >
      <Head>
        <title>{`${realEstateFund.name} | Fundamentei`}</title>
      </Head>

      <BackToTop />

      <div
        css={{
          display: "grid",
          gridRowGap: 16,
        }}
      >
        <div
          css={{
            display: "grid",
            gridTemplateColumns: "repeat(12, 1fr)",
            gridColumnGap: 30,
            gridRowGap: 48,
            alignItems: "center",
            margin: "0px auto",
            width: 960,
          }}
        >
          <div
            css={{
              gridColumn: "span 7",
              boxShadow: "0 4px 6px 0 #33333333",
              backgroundColor: "#ffffff",
              borderRadius: 4,
            }}
          >
            <div
              css={{
                padding: "24px 0px 16px 0px",
                display: "grid",
                gridRowGap: 20,
              }}
            >
              <div
                css={{
                  padding: "0px 24px",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <div
                  css={{
                    display: "grid",
                    gridRowGap: 4,
                  }}
                >
                  <div className="flex space-x-2">
                    <div>
                      <span
                        css={{
                          fontSize: 18,
                          fontWeight: "bold",
                          lineHeight: `${18 * 1.333}px`,
                          letterSpacing: 0.7,
                          color: "#333333",
                          display: "flex",
                        }}
                      >
                        {realEstateFund.name}
                      </span>
                      <span
                        css={{
                          fontSize: 16,
                          fontWeight: "bold",
                          lineHeight: `${16 * 1.333}px`,
                          letterSpacing: 0.7,
                          color: palette.blueBayoux,
                        }}
                      >
                        {realEstateFund.tickerSymbolPrefix}
                      </span>
                    </div>
                    <div>
                      <ToggleBookmarkButton
                        issuer={realEstateFund}
                        viewerDidBookmark={realEstateFund.viewerDidBookmark}
                      />
                    </div>
                  </div>

                  <span
                    css={{
                      fontSize: 12,
                      lineHeight: `${12 * 1.333}px`,
                      letterSpacing: 0.5,
                      color: palette.blueBayoux,
                      fontWeight: 500,
                    }}
                  >
                    {`CNPJ: ${realEstateFund.cnpj}`}
                  </span>
                </div>

                {realEstateFund.primaryLogo && (
                  <img
                    src={realEstateFund.primaryLogo.url}
                    width={158}
                    height={48}
                    alt={realEstateFund.name}
                    css={{
                      objectFit: "contain",
                      objectPosition: "right",
                      height: 48,
                    }}
                  />
                )}
              </div>

              <div
                css={{
                  padding: "0px 24px",
                  display: "grid",
                  gridAutoFlow: "column",
                  gridAutoColumns: "max-content",
                  gridColumnGap: 20,
                }}
              >
                <div>
                  <div
                    css={{
                      fontSize: 14,
                      fontWeight: "bold",
                      lineHeight: `${14 * 1.33}px`,
                      letterSpacing: 0.5,
                      color: palette.mineShaft,
                    }}
                  >
                    Início
                  </div>

                  <div
                    css={{
                      fontSize: 14,
                      fontWeight: 500,
                      lineHeight: `${14 * 1.33}px`,
                      letterSpacing: 0.5,
                      color: palette.lynch,
                      marginTop: 4,
                    }}
                  >
                    {realEstateFund.startOfOperation}
                  </div>
                </div>

                <div>
                  <div
                    css={{
                      color: palette.mineShaft,
                      fontWeight: "bold",
                      fontSize: 14,
                      lineHeight: `${14 * 1.33}px`,
                      letterSpacing: 0.5,
                    }}
                  >
                    Prazo
                  </div>

                  {realEstateFund.dateOfDuration !== null ? (
                    <div
                      css={{
                        marginTop: 4,
                        color: palette.mojo,
                        fontWeight: 700,
                        fontSize: 14,
                        lineHeight: `${14 * 1.33}px`,
                        letterSpacing: 0.5,
                      }}
                    >
                      {format(parseDateYmd(realEstateFund.dateOfDuration), "dd/MM/yyyy")}
                    </div>
                  ) : (
                    <div
                      css={{
                        marginTop: 4,
                        color: palette.lynch,
                        fontWeight: 500,
                        fontSize: 14,
                        lineHeight: `${14 * 1.33}px`,
                        letterSpacing: 0.5,
                      }}
                    >
                      Indeterminado
                    </div>
                  )}
                </div>

                {realEstateFund.audience && (
                  <div>
                    <div
                      css={{
                        fontSize: 14,
                        fontWeight: "bold",
                        lineHeight: `${14 * 1.33}px`,
                        letterSpacing: 0.5,
                        color: palette.mineShaft,
                      }}
                    >
                      Público-alvo
                    </div>

                    <div
                      css={{
                        marginTop: 4,
                        color: /Investidores em Geral/i.test(realEstateFund.audience) ? palette.lynch : palette.mojo,
                        fontWeight: /Investidores em Geral/i.test(realEstateFund.audience) ? 500 : 700,
                        fontSize: 14,
                        lineHeight: `${14 * 1.33}px`,
                        letterSpacing: 0.5,
                      }}
                    >
                      {realEstateFund.audience}
                    </div>
                  </div>
                )}
              </div>

              <div
                css={{
                  padding: "0px 24px",
                  display: "grid",
                  gridAutoFlow: "column",
                  gridAutoColumns: "max-content",
                  gridColumnGap: 20,
                }}
              >
                {realEstateFund.selfRegulation.primarySegment && (
                  <div>
                    <div
                      css={{
                        fontSize: 14,
                        fontWeight: "bold",
                        lineHeight: `${14 * 1.33}px`,
                        letterSpacing: 0.5,
                        color: palette.mineShaft,
                      }}
                    >
                      Segmento
                    </div>

                    <div
                      css={{
                        fontSize: 14,
                        fontWeight: 500,
                        lineHeight: `${14 * 1.33}px`,
                        letterSpacing: 0.5,
                        color: palette.lynch,
                        marginTop: 4,
                      }}
                    >
                      {realEstateFund.selfRegulation.primarySegment}
                    </div>
                  </div>
                )}

                {realEstateFund.classification.length > 0 && (
                  <div>
                    <div
                      css={{
                        fontSize: 14,
                        fontWeight: "bold",
                        lineHeight: `${14 * 1.33}px`,
                        letterSpacing: 0.5,
                        color: palette.mineShaft,
                      }}
                    >
                      Tipo
                    </div>

                    <div
                      css={{
                        fontSize: 14,
                        fontWeight: 500,
                        lineHeight: `${14 * 1.33}px`,
                        letterSpacing: 0.5,
                        color: palette.lynch,
                        marginTop: 4,
                      }}
                    >
                      {realEstateFund.classification.join(" / ")}
                    </div>
                  </div>
                )}

                {realEstateFund.selfRegulation.mandate && (
                  <div>
                    <div
                      css={{
                        fontSize: 14,
                        fontWeight: "bold",
                        lineHeight: `${14 * 1.33}px`,
                        letterSpacing: 0.5,
                        color: palette.mineShaft,
                      }}
                    >
                      Mandato
                    </div>

                    <div
                      css={{
                        fontSize: 14,
                        fontWeight: 500,
                        lineHeight: `${14 * 1.33}px`,
                        letterSpacing: 0.5,
                        color: palette.lynch,
                        marginTop: 4,
                      }}
                    >
                      {realEstateFund.selfRegulation.mandate}
                    </div>
                  </div>
                )}
              </div>

              {realEstateFund.marketCapitalization && realEstateFund.marketCapitalization.totalCapitalization > 0 && (
                <div
                  css={{
                    padding: "0px 24px",
                    display: "grid",
                    gridRowGap: 4,
                  }}
                >
                  <div
                    css={{
                      fontSize: 14,
                      fontWeight: "bold",
                      lineHeight: 1.33,
                      letterSpacing: 0.5,
                      color: palette.mineShaft,
                    }}
                  >
                    Market Cap
                  </div>

                  <div
                    title={String(realEstateFund.marketCapitalization.totalCapitalization)}
                    css={{
                      display: "inline-flex",
                      fontSize: 14,
                      lineHeight: 1.33,
                      fontWeight: 500,
                      letterSpacing: 0.5,
                      color: palette.lynch,
                    }}
                  >
                    {formatMoneyValue(realEstateFund.marketCapitalization.totalCapitalization, "long", "pt-BR")}
                  </div>
                </div>
              )}

              {(realEstateFund.ifixParticipation || realEstateFund.operatingSegment !== "Tradicional") && (
                <div
                  css={{
                    display: "grid",
                    gridAutoColumns: "max-content",
                    gridAutoFlow: "column",
                    gridColumnGap: 20,
                    padding: "0px 24px",
                  }}
                >
                  {realEstateFund.ifixParticipation && (
                    <div>
                      <div
                        css={{
                          color: palette.mineShaft,
                          fontWeight: "bold",
                          fontSize: 14,
                          lineHeight: `${14 * 1.33}px`,
                          letterSpacing: 0.5,
                        }}
                      >
                        % IFIX
                      </div>

                      <div
                        css={{
                          marginTop: 4,
                          color: palette.lynch,
                          fontWeight: 500,
                          fontSize: 14,
                          lineHeight: `${14 * 1.33}px`,
                          letterSpacing: 0.5,
                        }}
                      >
                        {realEstateFund.ifixParticipation.asPercentage.toFixed(2).replace(".", ",")}%
                      </div>
                    </div>
                  )}

                  {realEstateFund.operatingSegment !== "Tradicional" && (
                    <div>
                      <div
                        css={{
                          fontSize: 14,
                          fontWeight: "bold",
                          lineHeight: `${14 * 1.33}px`,
                          letterSpacing: 0.5,
                          color: palette.mineShaft,
                        }}
                      >
                        Listagem
                      </div>

                      <div
                        css={{
                          fontSize: 14,
                          fontWeight: 500,
                          lineHeight: `${14 * 1.33}px`,
                          letterSpacing: 0.5,
                          color: palette.lynch,
                          marginTop: 4,
                        }}
                      >
                        {realEstateFund.operatingSegment}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {realEstateFund.shortDescription && (
                <div
                  css={{
                    display: "grid",
                    padding: "0px 24px",
                  }}
                >
                  <div>
                    <div
                      css={{
                        color: palette.mineShaft,
                        fontWeight: "bold",
                        fontSize: 14,
                        lineHeight: 1.33,
                        letterSpacing: 0.5,
                      }}
                    >
                      Características
                    </div>

                    <div
                      css={{
                        marginTop: 4,
                        color: palette.lynch,
                        fontWeight: 500,
                        fontSize: 14,
                        lineHeight: 1.33,
                        letterSpacing: 0.5,
                      }}
                    >
                      {realEstateFund.shortDescription}
                    </div>
                  </div>
                </div>
              )}

              {realEstateFund.overview.totalOfRealEstateProperties > 0 ||
                realEstateFund.overview.totalOfTenants > 0 ||
                (realEstateFund.overview.totalOfRegions > 0 && (
                  <div
                    css={{
                      padding: "0px 24px",
                      display: "grid",
                      gridAutoFlow: "column",
                      gridAutoColumns: "max-content",
                      gridColumnGap: 20,
                    }}
                  >
                    {realEstateFund.overview.totalOfRealEstateProperties !== 0 && (
                      <div>
                        <div
                          css={{
                            fontSize: 14,
                            fontWeight: "bold",
                            lineHeight: `${14 * 1.33}px`,
                            letterSpacing: 0.5,
                            color: palette.mineShaft,
                          }}
                        >
                          Imóveis
                        </div>

                        <div
                          css={{
                            fontSize: 14,
                            fontWeight: 500,
                            lineHeight: `${14 * 1.33}px`,
                            letterSpacing: 0.5,
                            color: palette.lynch,
                            marginTop: 4,
                          }}
                        >
                          {realEstateFund.overview.totalOfRealEstateProperties === 1 ? "Mono" : "Multi"}
                        </div>
                      </div>
                    )}

                    {realEstateFund.overview.totalOfTenants !== 0 && (
                      <div>
                        <div
                          css={{
                            fontSize: 14,
                            fontWeight: "bold",
                            lineHeight: `${14 * 1.33}px`,
                            letterSpacing: 0.5,
                            color: palette.mineShaft,
                          }}
                        >
                          Inquilinos
                        </div>

                        <div
                          css={{
                            fontSize: 14,
                            fontWeight: 500,
                            lineHeight: `${14 * 1.33}px`,
                            letterSpacing: 0.5,
                            color: palette.lynch,
                            marginTop: 4,
                          }}
                        >
                          {realEstateFund.overview.totalOfTenants === 1 ? "Mono" : "Multi"}
                        </div>
                      </div>
                    )}

                    {realEstateFund.overview.totalOfRegions !== 0 && (
                      <div>
                        <div
                          css={{
                            fontSize: 14,
                            fontWeight: "bold",
                            lineHeight: `${14 * 1.33}px`,
                            letterSpacing: 0.5,
                            color: palette.mineShaft,
                          }}
                        >
                          Regiões
                        </div>

                        <div
                          css={{
                            fontSize: 14,
                            fontWeight: 500,
                            lineHeight: `${14 * 1.33}px`,
                            letterSpacing: 0.5,
                            color: palette.lynch,
                            marginTop: 4,
                          }}
                        >
                          {realEstateFund.overview.totalOfRegions === 1 ? "Mono" : "Multi"}
                        </div>
                      </div>
                    )}
                  </div>
                ))}

              {realEstateFund.managementReport && (
                <div
                  css={{
                    display: "grid",
                    padding: "0px 24px",
                  }}
                >
                  <div>
                    <div
                      css={{
                        color: palette.mineShaft,
                        fontWeight: "bold",
                        fontSize: 14,
                        lineHeight: 1.33,
                        letterSpacing: 0.5,
                      }}
                    >
                      Relatório Gerencial
                    </div>

                    <div
                      css={{
                        marginTop: 4,
                        color: palette.lynch,
                        fontWeight: 500,
                        fontSize: 14,
                        lineHeight: 1.33,
                        letterSpacing: 0.5,
                      }}
                    >
                      <a
                        title="Relatório Gerencial"
                        target="_blank"
                        href={realEstateFund.managementReport.linkHref}
                        rel="noopener noreferrer"
                        css={{
                          fontSize: 14,
                          fontWeight: 500,
                          lineHeight: `${14 * 1.33}px`,
                          letterSpacing: 0.8,
                          color: palette.link,
                          textDecoration: "none",
                          "&:hover": {
                            textDecoration: "underline",
                          },
                        }}
                      >
                        Referência:{" "}
                        {format(parseDateYmd(realEstateFund.managementReport.dateOfReference), "dd/MM/yyyy")}
                      </a>
                    </div>
                  </div>
                </div>
              )}

              <div
                css={{
                  padding: "0px 24px",
                }}
              >
                <div
                  css={{
                    display: "grid",
                    gridAutoColumns: "max-content",
                    gridAutoFlow: "column",
                    gridColumnGap: 8,
                    color: palette.lynch,
                  }}
                >
                  {realEstateFund.website && (
                    <>
                      <a
                        title="Site de RI "
                        target="_blank"
                        href={realEstateFund.website}
                        rel="noopener noreferrer"
                        css={{
                          fontSize: 14,
                          fontWeight: 500,
                          lineHeight: `${14 * 1.33}px`,
                          letterSpacing: 0.8,
                          color: palette.link,
                          textDecoration: "none",
                          "&:hover": {
                            textDecoration: "underline",
                          },
                        }}
                      >
                        Site do Fundo
                      </a>

                      <span>•</span>
                    </>
                  )}

                  <a
                    title="Site da B3"
                    target="_blank"
                    href={realEstateFund.b3Href}
                    rel="noopener noreferrer"
                    css={{
                      fontSize: 14,
                      fontWeight: 500,
                      lineHeight: `${14 * 1.33}px`,
                      letterSpacing: 0.8,
                      color: palette.link,
                      textDecoration: "none",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Site B3
                  </a>

                  <span>•</span>

                  <a
                    title="Site da B3"
                    target="_blank"
                    href={realEstateFund.fundosNetHref}
                    rel="noopener noreferrer"
                    css={{
                      fontSize: 14,
                      fontWeight: 500,
                      lineHeight: `${14 * 1.33}px`,
                      letterSpacing: 0.8,
                      color: palette.link,
                      textDecoration: "none",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Arquivos
                  </a>
                </div>
              </div>

              <div
                css={{
                  display: "grid",
                  padding: "0px 24px",
                  gridRowGap: 16,
                }}
              >
                <div
                  css={{
                    display: "grid",
                    gridAutoFlow: "column",
                    gridAutoColumns: "max-content auto",
                    gridColumnGap: 20,
                    overflow: "hidden",
                  }}
                >
                  {realEstateFund.selfRegulation.management && (
                    <div>
                      <div
                        css={{
                          fontSize: 14,
                          fontWeight: "bold",
                          lineHeight: `${14 * 1.33}px`,
                          letterSpacing: 0.5,
                          color: palette.mineShaft,
                        }}
                      >
                        Gestão
                      </div>

                      <div
                        css={{
                          fontSize: 14,
                          fontWeight: 600,
                          lineHeight: `${14 * 1.33}px`,
                          letterSpacing: 0.5,
                          color: /Ativa/i.test(realEstateFund.selfRegulation.management)
                            ? palette.scienceBlue
                            : palette.copper,
                          marginTop: 4,
                        }}
                      >
                        {realEstateFund.selfRegulation.management}
                      </div>
                    </div>
                  )}

                  {realEstateFund.manager && (
                    <div
                      css={{
                        display: "grid",
                        gridRowGap: 4,
                      }}
                    >
                      <div
                        css={{
                          fontSize: 14,
                          fontWeight: "bold",
                          lineHeight: `${14 * 1.33}px`,
                          letterSpacing: 0.5,
                          color: palette.mineShaft,
                        }}
                      >
                        Gestor
                      </div>

                      <div
                        css={{
                          fontSize: 14,
                          fontWeight: 500,
                          lineHeight: 1.333,
                          letterSpacing: 0.5,
                          color: palette.lynch,

                          minWidth: 0,
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                        }}
                      >
                        <span title={realEstateFund.manager.name}>{realEstateFund.manager.name}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div
                  css={{
                    display: "grid",
                    gridRowGap: 4,
                  }}
                >
                  <div
                    css={{
                      fontSize: 14,
                      fontWeight: "bold",
                      lineHeight: 1.33,
                      letterSpacing: 0.5,
                      color: palette.mineShaft,
                    }}
                  >
                    Administrador
                  </div>

                  <div
                    css={{
                      fontSize: 14,
                      fontWeight: 500,
                      lineHeight: 1.33,
                      letterSpacing: 0.5,
                      color: palette.lynch,
                    }}
                  >
                    {realEstateFund.administrator.name}
                  </div>
                </div>

                <div>
                  <button
                    type="button"
                    onClick={() => setShowCompensationPolicy(!showCompensationPolicy)}
                    css={{
                      display: "inline-flex",
                      margin: 0,
                      border: "none",
                      width: "auto",
                      padding: 0,
                      background: "none",
                      cursor: "pointer",
                      outline: "none",
                      fontSize: 14,
                      lineHeight: `${14 * 1.333}px`,
                      letterSpacing: 0.5,
                      color: palette.link,
                      userSelect: "none",
                      "&:focus": {
                        color: palette.linkVisited,
                      },
                    }}
                  >
                    {showCompensationPolicy
                      ? "Esconder política de remuneração da administração"
                      : "Visualizar política de remuneração da administração"}
                  </button>
                </div>
              </div>

              {showCompensationPolicy && (
                <AdministrationCompensationPolicy
                  compensationPolicy={realEstateFund.administrator.compensationPolicy}
                />
              )}

              {realEstateFund.distributionOfAssets && <DistributionOfAssets realEstateFund={realEstateFund} />}
            </div>
          </div>
        </div>
      </div>

      <div
        css={{
          display: "grid",
          gridTemplateColumns: "repeat(12, 1fr)",
          gridColumnGap: 30,
          gridRowGap: 48,
          alignItems: "center",
          margin: "0px auto",
          width: 960,
        }}
      >
        <div
          css={{
            gridColumn: "span 7",
            boxShadow: "0 4px 6px 0 #33333333",
            backgroundColor: "#ffffff",
            borderRadius: 4,
          }}
        >
          <div css={{ padding: 24 }}>
            <h6
              css={{
                display: "flex",
                margin: 0,
                color: shades.greys.variants[2],
                fontWeight: "bold",
                fontSize: 14,
                fontFamily: "museo-sans-condensed",
                lineHeight: 1.33,
                letterSpacing: 1,
                textTransform: "uppercase",
              }}
            >
              Avaliação dos Investidores
            </h6>

            <div css={{ marginTop: 8 }}>
              <Rating
                issuer={realEstateFund}
                rankingPlacement={realEstateFund.overallRankingPlacement}
                rankingRatingSummary={realEstateFund.overallRankingRatingSummary}
              />
            </div>
          </div>
        </div>
      </div>

      <div
        css={{
          display: "grid",
          gridTemplateColumns: "repeat(12, 1fr)",
          gridColumnGap: 30,
          gridRowGap: 52,
          alignItems: "center",
          margin: "0px auto",
          width: 1140,
        }}
      >
        {realEstateFund.tickerSymbol && realEstateFund.tickerSymbol.length > 0 && (
          <Liquidity
            key={realEstateFund._id}
            asset={realEstateFund}
            namespace={IAssetNamespace.Bra}
            tickerSymbols={[realEstateFund.tickerSymbol]}
          />
        )}
      </div>

      {hasPropertiesToRent(realEstateFund.properties) && (
        <div
          css={{
            display: "grid",
            gridTemplateColumns: "repeat(12, 1fr)",
            alignItems: "center",
            width: 960,
            margin: "0px auto",
          }}
        >
          <div>
            <PropertiesToRent realEstateFund={realEstateFund} />

            {realEstateFund.lastQuarterlyReportDate && (
              <div
                css={{
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <div
                  css={{
                    display: "inline-flex",
                    padding: 16,
                    backgroundColor: palette.catskillWhite,
                    borderRight: `solid 1px ${palette.mystic}`,
                    borderBottom: `solid 1px ${palette.mystic}`,
                    borderLeft: `solid 1px ${palette.mystic}`,
                    borderBottomRightRadius: 4,
                    borderBottomLeftRadius: 4,
                  }}
                >
                  <p
                    css={{
                      margin: 0,
                      color: palette.lynch,
                      fontSize: 14,
                      lineHeight: 1.33,
                      letterSpacing: 1,
                    }}
                  >
                    Os dados acima foram atualizados com o último Informe Trimestral divulgado pelo fundo em{" "}
                    {format(parseDateYmd(realEstateFund.lastQuarterlyReportDate), "dd/MM/yyyy")}.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {realEstateFund.financials && (
        <>
          <div
            css={{
              margin: "0px auto",
            }}
          >
            <div
              css={{
                position: "relative",
                display: "inline-flex",
                backgroundColor: "#ffffff",
                borderRadius: 4,
                boxShadow: "0 4px 6px 0 #33333333",
              }}
            >
              <RealEstateFundFinancials
                displayAllColumns={user.isPremium}
                financials={realEstateFund.financials.monthly}
              />
            </div>

            <div
              css={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <div
                css={{
                  display: "inline-flex",
                  padding: 16,
                  backgroundColor: palette.catskillWhite,
                  borderRight: `solid 1px ${palette.mystic}`,
                  borderBottom: `solid 1px ${palette.mystic}`,
                  borderLeft: `solid 1px ${palette.mystic}`,
                  borderBottomRightRadius: 4,
                  borderBottomLeftRadius: 4,
                }}
              >
                <p
                  css={{
                    margin: 0,
                    color: palette.lynch,
                    fontSize: 14,
                    lineHeight: 1.33,
                    letterSpacing: 1,
                  }}
                >
                  Os valores acima estão em BRL (reais) e na escala de milhões — 10.000 equivalem à 10 bilhões de reais.
                </p>
              </div>
            </div>
          </div>

          {realEstateFund.financials.monthly.length > 3 && (
            <BrazilianRealEstateFundFundamentalsChart financials={realEstateFund.financials.monthly} />
          )}
        </>
      )}

      {realEstateFund.analysisVideo && <YouTubeAnalysisVideo analysisVideo={realEstateFund.analysisVideo} />}

      <div
        css={{
          paddingBottom: 48,
        }}
      >
        <AssetDiscussionGridContainer gridColumn="span 12">
          <AssetDiscussion assetType={IAssetType.BrazilianRealEstateFund} assetId={realEstateFund._id} />
        </AssetDiscussionGridContainer>
      </div>
    </div>
  );
}

function hasPropertiesToRent(properties: IBrazilianRealEstateFundTopLevelFragment["properties"]): boolean {
  return properties.some((property) => {
    return property.__typename === "BrazilianRealEstateFundPropertyToRent";
  });
}

interface IAdministrationCompensationPolicyProps {
  compensationPolicy: string;
}

function AdministrationCompensationPolicy({ compensationPolicy }: IAdministrationCompensationPolicyProps) {
  return (
    <div
      css={{
        display: "grid",
        padding: 24,
        backgroundColor: palette.catskillWhite,
        gridRowGap: 12,
        boxShadow: "inset 0 8px 4px -8px rgba(0, 0, 0, 0.4), inset 0 -8px 4px -8px rgba(0, 0, 0, 0.2)",
      }}
    >
      <h6
        css={{
          margin: 0,
          fontSize: 18,
          lineHeight: `${18 * 1.333}px`,
          letterSpacing: 0.5,
          color: palette.pickledBluewood,
        }}
      >
        Política de Remuneração da Administração
      </h6>

      <p
        css={{
          margin: 0,
          fontSize: 14,
          letterSpacing: 0.8,
          color: palette.lynch,
          lineHeight: `${18 * 1.333}px`,
        }}
      >
        {compensationPolicy}
      </p>
    </div>
  );
}
